<template>
  <div class="aa-faq-app__list">

    <!-- Filter Row -->
    <div class="aa-faq-app__filters row" v-if="!id">
      <!-- Tag Filters -->
      <div class="col-lg-9">
        <div class="aa-faq-app__tag-filters">
          <label
              v-for="(label, key) in filteredTags"
              :key="key"
              class="btn btn-outline-primary"
              :class="[this.selectedTag === key ? 'active' : '']"
              :for="key"
          >
            <input
                type="radio"
                name="aa-faq-tag-filters"
                :value="key"
                v-model="selectedTag"
                class="btn-check"
                :id="key"
                autocomplete="off"
            />
            {{ label }}
          </label>

          <!-- Clear tag filters -->
          <button @click="this.clearTags()" class="btn btn-outline-success">Clear</button>
        </div>
      </div>


      <!-- Search Input -->
      <div class="col-lg-3">
        <div class="search-box">
          <input
              type="text"
              class="form-control mt-3 mt-lg-0"
              v-model="searchQuery"
              placeholder="Search FAQs..."
              @input="updateSearchQuery"
          />
        </div>
      </div>
    </div>

    <!-- Accordion -->
    <div class="aa-accordion aa-accordion--faq" id="aa-faq-accordion">
      <template v-for="faq in filteredFaqs" :key="faq.id">
        <div class="accordion-item">

          <!-- Accordion header -->
          <h3 class="accordion-header" :id="`afa-heading-${faq.id}`">
            <button
                class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                :data-bs-target="`#afa-content-${faq.id}`"
                :aria-expanded="id ? 'true' : 'false'"
                :aria-controls="`#afa-content-${faq.id}`">

              <i class="fa-regular fa-circle-question"></i>

              <span class="aa-ac-header-content">{{ faq.question }}</span>

              <span v-if="faq.tags.length > 0" class="aa-accordion__tags">
                  <span
                      v-for="tagKey in faq.tags"
                      :key="tagKey"
                      class="tag tag-grey tag--small"
                  >
                    {{ tagLabel(tagKey) }}
                  </span>
              </span>

            </button>
          </h3>

          <!-- Accordion content -->
          <div :id="`afa-content-${faq.id}`" :class="`accordion-collapse collapse ${id ? 'show' : ''}`" :aria-labelledby="`#afa-heading-${faq.id}`" data-bs-parent="#aa-faq-accordion">
            <div class="accordion-body">
              <i class="fa-regular fa-message"></i>
              <div class="aa-ac-body-content" v-html="faq.answer" />
            </div>
          </div>

        </div>
      </template>
    </div>

    <div v-if="filteredFaqs.length === 0" class="aa-faq-app__no-results">
      <i class="fa-solid fa-ban"></i>
      No results for this filter combination.
    </div>
  </div>
</template>

<script>
// @todo This should only be included when not in Drupal context.
import testFaqs from '@/data/faqs.json';

export default {
  props: {
    id: String,
    customerGroup: String,
    tag: null,
    query: String,
  },
  data() {
    return {
      // Initialise with tag from the route if any.
      selectedTag: this.tag ? this.tag : null,
      // Initialise the full query with text and tags.
      searchQuery: this.query || '',
    };
  },
  computed: {
    // Filter the FAQs by customer group and tag.
    filteredFaqs() {
      // Get the base filter set by customer.
      let filtered = this.filterByCustomer();

      // Filter by selected tag.
      if (this.selectedTag) {
        filtered = filtered.filter(faq =>
            faq.tags.includes(this.selectedTag)
        );
      }

      // Filter by search query (matches question or answer).
      if (this.searchQuery) {
        const searchTerm = this.searchQuery.toLowerCase();
        filtered = filtered.filter(faq => {
          return faq.question.toLowerCase().includes(searchTerm) ||
              faq.answer.toLowerCase().includes(searchTerm);
        });
      }

      return filtered;
    },
    allTags() {
      if (window.drupalSettings?.aaFaq?.tags) {
        // If settings are available, use the Drupal data.
        return window.drupalSettings.aaFaq.tags;
      } else {
        // When developing outside the Drupal context, use test data.
        // Get static data for now.
        return {
          'structure': 'Structure',
          'how_it_works': 'How it works',
          'fees': 'Fees',
          'whats_allowed': 'What\'s allowed',
          'managing_lps': 'Managing LPs',
          'tax': 'Tax',
          'help_learning': 'Help/Learning',
          'recommendations': 'Recommendations',
        };
      }

    },
    // Dynamically generate available tags based on filtered FAQs by customer.
    // This looks into the customer set and only adds tag filters that are
    // present in the current result set.
    filteredTags() {
      const targetTags = {};
      const filteredByCustomer = this.filterByCustomer();
      filteredByCustomer.forEach(faq => {
        faq.tags.forEach(tag => targetTags[tag] = this.tagLabel(tag));
      });

      return targetTags;
    }
  },
  methods: {
    // Get the full FAQ dataset form settings or test JSON.
    getFaqs() {
      if (window.drupalSettings?.aaFaq?.data) {
        // If settings are available, use the Drupal data.
        return window.drupalSettings.aaFaq.data;
      } else {
        // When developing outside the Drupal context, use test data.
        // Get static data for now.
        return testFaqs;
      }
    },
    filterByCustomer() {
      let filtered = this.getFaqs();

      // If the ID is present, return only the matching FAQ.
      if (this.id) {
        const faq = filtered.find(faq => parseInt(faq.id) === parseInt(this.id));
        return faq ? [faq] : [];
      }

      // Sanity base filter to check that both Q & A exist.
      filtered = filtered.filter(faq =>
          faq.question !== null &&
          faq.answer !== null
      );

      if (this.customerGroup) {
        filtered = filtered.filter(faq => faq.customer_group.includes(this.customerGroup));
      }

      return filtered;
    },
    // Clear tag radio buttons and query string.
    clearTags() {
      this.selectedTag = null;
      this.$router.push({
        query: {}
      });
    },
    // updateSearchQuery() {
    //   // This method triggers the watch to update the URL.
    //   // It's called every time the input changes.
    // },
    tagLabel(key) {
      const tagMap = this.allTags;
      return tagMap[key];
    }
  },
  watch: {
    // Add selected tag to query string.
    selectedTag(newTag) {
      this.$router.push({
        query: {
          tag: newTag ? newTag : undefined,
          query: this.searchQuery ? this.searchQuery : undefined
        }
      });
    },

    // Add text search to query string.
    searchQuery(newQuery) {
      this.$router.push({
        query: {
          tag: this.selectedTag ? this.selectedTag : undefined,
          query: newQuery ? newQuery : undefined
        }
      });
    },

    // Clear any selected tags when changing customer group.
    $route(to, from) {
      if (to.path !== from.path) {
        this.selectedTag = null;
        this.searchQuery = null;
      }
    },

    filteredFaqs(newFaqs) {
      // Emit the count of filteredFaqs whenever they change.
      this.$emit('update-faq-page', {
        faqCount: newFaqs.length,
        tagLabel: this.tagLabel(this.selectedTag)
      });
    },
  },
  mounted() {
    // Emit the initial FAQ count on component mount.
    this.$emit('update-faq-page', {
      faqCount: this.filteredFaqs.length,
      tagLabel: this.tagLabel(this.selectedTag)
    });
  },
};
</script>

<style>

</style>
